import {
  addDays,
  addHours,
  addMonths,
  addWeeks,
  addYears,
  differenceInDays,
} from "date-fns";
import { formatInTimeZone } from "date-fns-tz";

import { EventQueryInterval } from "./types";

// isoWithOffsetFormat preserves the wall-clock + TZ offset of a Date as a
// round-trippable ISO8601 string (e.g. "2026-04-04T00:00:00.000-05:00").
// Using `toISOString()` would coerce to UTC ("...Z") and drop the offset,
// which forces every downstream layer — URL params, Go parse, SQL bind,
// Snowflake filter — to agree on a separate UTC-vs-local reinterpretation.
// This codebase's invariant is that the literal timestamp and its literal
// offset flow unchanged through every layer; zero reinterpretation.
const isoWithOffsetFormat = "yyyy-MM-dd'T'HH:mm:ss.SSSxxx";

export type RelativeDateUnit = "h" | "d" | "w" | "m" | "y";

export const regexRelativeDate = new RegExp(
  `^(-?[0-9]+)?([hdwmy])(Start|End)?$`,
);

export function relativeDateUnitToString(u: RelativeDateUnit): string {
  switch (u) {
    case "h":
      return "hours";
    case "d":
      return "days";
    case "w":
      return "weeks";
    case "m":
      return "months";
    case "y":
      return "years";
  }
}

type RelativeDateIntervalPosition = "Start" | "End";

type RelativeDateExpression = {
  num: number;
  unit: RelativeDateUnit;
  intervalPosition?: RelativeDateIntervalPosition;
};

export class DateRange {
  public readonly from: Date | RelativeDateExpression;
  public readonly to?: Date | RelativeDateExpression;
  private readonly clientTimeZone: string;

  constructor(
    from: Date | RelativeDateExpression,
    to?: Date | RelativeDateExpression,
    clientTimeZone: string = window.Intl.DateTimeFormat().resolvedOptions()
      .timeZone,
  ) {
    this.from = from;
    this.to = to;
    this.clientTimeZone = clientTimeZone;
  }

  public equals(other: DateRange): boolean {
    return JSON.stringify(other.asJSON()) === JSON.stringify(this.asJSON());
  }

  public asJSON(): {
    date_from: string;
    date_to?: string;
    clientTimeZone: string;
  } {
    const encode = (v: Date | RelativeDateExpression): string =>
      v instanceof Date
        ? formatInTimeZone(v, this.clientTimeZone, isoWithOffsetFormat)
        : this.relativeDateExpressionToJSONString(v);

    if (this.to === undefined) {
      return {
        clientTimeZone: this.clientTimeZone,
        date_from: encode(this.from),
      };
    }

    return {
      clientTimeZone: this.clientTimeZone,
      date_from: encode(this.from),
      date_to: encode(this.to),
    };
  }

  private static parseDateRangeValueFromQueryParams(
    queryParams: URLSearchParams,
    key: string,
  ): Date | RelativeDateExpression | undefined {
    const rawValue = (queryParams.get(key) ?? "").trim();
    if (rawValue === "") {
      return undefined;
    }

    if (regexRelativeDate.test(rawValue)) {
      const [, num, unit, intervalPosition] = regexRelativeDate.exec(
        rawValue,
      ) as RegExpExecArray;

      const parsedNum = num === undefined ? 0 : parseInt(num, 10);
      // If the number is not a number or is positive, it's invalid. We can't query into the future. Zero is valid
      // because a relative expression for the current hour, day, etc. is valid, and would have a zero value.
      if (isNaN(parsedNum) || parsedNum > 0) {
        return undefined;
      }

      return {
        num: parsedNum,
        unit: unit as RelativeDateUnit,
        intervalPosition: intervalPosition as RelativeDateIntervalPosition,
      };
    }

    // Try to parse as a date. We expect ISO8601 format. This obviously doesn't guarantee that, but anything that looks
    // like a date string to `Date` will be parsed.
    const parsedDate = new Date(rawValue);
    if (isNaN(parsedDate.getTime())) {
      return undefined;
    }

    return parsedDate;
  }

  public static fromURLSearchParams(
    params: URLSearchParams,
    fromKey: string = "from",
    toKey: string = "to",
    clientTimeZone: string = window.Intl.DateTimeFormat().resolvedOptions()
      .timeZone,
  ): DateRange {
    return new DateRange(
      this.parseDateRangeValueFromQueryParams(params, fromKey) ??
        dateRangeLast7Days(clientTimeZone).from,
      this.parseDateRangeValueFromQueryParams(params, toKey),
      clientTimeZone,
    );
  }

  private relativeDateExpressionToJSONString(
    x: RelativeDateExpression,
  ): string {
    return `${x.num}${x.unit}${x.intervalPosition ? x.intervalPosition : ""}`;
  }
}

export const dateRangeToday = (
  clientTimeZone: string = window.Intl.DateTimeFormat().resolvedOptions()
    .timeZone,
) =>
  new DateRange(
    { num: 0, unit: "d", intervalPosition: "Start" },
    { num: 0, unit: "d", intervalPosition: "End" },
    clientTimeZone,
  );

export const dateRangeYesterday = (
  clientTimeZone: string = window.Intl.DateTimeFormat().resolvedOptions()
    .timeZone,
) =>
  new DateRange(
    { num: -1, unit: "d", intervalPosition: "Start" },
    { num: -1, unit: "d", intervalPosition: "End" },
    clientTimeZone,
  );

export const dateRangeLast24Hours = (
  clientTimeZone: string = window.Intl.DateTimeFormat().resolvedOptions()
    .timeZone,
) =>
  new DateRange(
    {
      num: -24,
      unit: "h",
      intervalPosition: "Start",
    },
    undefined,
    clientTimeZone,
  );

export const dateRangeLast7Days = (
  clientTimeZone: string = window.Intl.DateTimeFormat().resolvedOptions()
    .timeZone,
) =>
  new DateRange(
    {
      num: -7,
      unit: "d",
    },
    undefined,
    clientTimeZone,
  );

export const dateRangeLast14Days = (
  clientTimeZone: string = window.Intl.DateTimeFormat().resolvedOptions()
    .timeZone,
) =>
  new DateRange(
    {
      num: -14,
      unit: "d",
    },
    undefined,
    clientTimeZone,
  );

export const dateRangeLast30Days = (
  clientTimeZone: string = window.Intl.DateTimeFormat().resolvedOptions()
    .timeZone,
) =>
  new DateRange(
    {
      num: -30,
      unit: "d",
    },
    undefined,
    clientTimeZone,
  );

export const dateRangeLast60Days = (
  clientTimeZone: string = window.Intl.DateTimeFormat().resolvedOptions()
    .timeZone,
) =>
  new DateRange(
    {
      num: -60,
      unit: "d",
    },
    undefined,
    clientTimeZone,
  );

export const dateRangeLast90Days = (
  clientTimeZone: string = window.Intl.DateTimeFormat().resolvedOptions()
    .timeZone,
) =>
  new DateRange(
    {
      num: -90,
      unit: "d",
    },
    undefined,
    clientTimeZone,
  );

export const dateRangeLast180Days = (
  clientTimeZone: string = window.Intl.DateTimeFormat().resolvedOptions()
    .timeZone,
) =>
  new DateRange(
    {
      num: -180,
      unit: "d",
    },
    undefined,
    clientTimeZone,
  );

export const dateRangeThisMonth = (
  clientTimeZone: string = window.Intl.DateTimeFormat().resolvedOptions()
    .timeZone,
) =>
  new DateRange(
    { num: 0, unit: "m", intervalPosition: "Start" },
    { num: 0, unit: "m", intervalPosition: "End" },
    clientTimeZone,
  );

export const dateRangeYearToDate = (
  clientTimeZone: string = window.Intl.DateTimeFormat().resolvedOptions()
    .timeZone,
) =>
  new DateRange(
    {
      num: 0,
      unit: "y",
      intervalPosition: "Start",
    },
    undefined,
    clientTimeZone,
  );

export const allPredefinedDateRanges = (
  clientTimeZone: string = window.Intl.DateTimeFormat().resolvedOptions()
    .timeZone,
) => [
  dateRangeToday(clientTimeZone),
  dateRangeYesterday(clientTimeZone),
  dateRangeLast24Hours(clientTimeZone),
  dateRangeLast7Days(clientTimeZone),
  dateRangeLast14Days(clientTimeZone),
  dateRangeLast30Days(clientTimeZone),
  dateRangeLast90Days(clientTimeZone),
  dateRangeLast180Days(clientTimeZone),
  dateRangeThisMonth(clientTimeZone),
  dateRangeYearToDate(clientTimeZone),
];

function approximateDate(date: Date | RelativeDateExpression): Date {
  if (date instanceof Date) {
    return date;
  }

  const { num, unit } = date;
  const now = new Date();

  switch (unit) {
    case "h":
      return addHours(now, num);
    case "d":
      return addDays(now, num);
    case "w":
      return addWeeks(now, num);
    case "m":
      return addMonths(now, num);
    case "y":
      return addYears(now, num);
  }
}

function approxDateRangeSizeInDays(dateRange: DateRange): number {
  const approxFrom = approximateDate(dateRange.from);
  const approxTo = approximateDate(dateRange.to ?? new Date());

  return Math.abs(differenceInDays(approxFrom, approxTo));
}

export function intervalOptionsForDateRange(
  dateRange: DateRange,
): [EventQueryInterval, EventQueryInterval] {
  const approxDays = approxDateRangeSizeInDays(dateRange);

  if (approxDays <= 1) {
    return ["day", "hour"];
  }

  if (approxDays <= 7) {
    return ["day", "hour"];
  }

  if (approxDays <= 30) {
    return ["day", "week"];
  }

  return ["month", "week"];
}

export function defaultIntervalForDateRange(
  dateRange: DateRange,
): EventQueryInterval {
  return intervalOptionsForDateRange(dateRange)[0];
}

export const intervalNamePlural: Record<EventQueryInterval, string> = {
  hour: "Hours",
  day: "Days",
  week: "Weeks",
  month: "Months",
};
